import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { OidcIdentityContext } from '@rsmus/react-auth';
import { useRecoilValue } from 'recoil';
import { userCEMState } from '../../recoil/userCEMState';
import { envProperties } from '../../util/EnvironmentProperties';
import { getNexusChatRole } from './nexusChatRole';
import './NexusChatLauncher.scss';

interface FederationContainer {
  get: (module: string) => Promise<() => unknown>;
  init: (shareScope: Record<string, unknown>) => Promise<void> | void;
}

interface ChatRemoteModule {
  acquireSilentToken: (options: { loginHint: string; authRedirectUri: string }) => Promise<string | null>;
  mount: (
    container: HTMLElement,
    options: {
      appId: string;
      role: 'Admin' | 'User';
      containerUser: { name?: string; email?: string };
      useMemoryRouter: boolean;
      authRedirectUri: string;
      accessToken: string | null;
      acquireAccessToken?: () => Promise<string>;
      onClose: () => void;
      topOffset: string;
      leftOffset: string;
      bottomOffset: string;
    },
  ) => void;
  unmount: () => void;
}

// Loaded once per page session; re-initialising a federation container is not supported.
let remoteModulePromise: Promise<ChatRemoteModule> | null = null;
const PRELOADED_TOKEN_MAX_AGE_MS = 5 * 60 * 1000;

function loadChatRemote(remoteUrl: string): Promise<ChatRemoteModule> {
  if (!remoteModulePromise) {
    remoteModulePromise = (async () => {
      const baseUrl = remoteUrl.endsWith('/') ? remoteUrl : `${remoteUrl}/`;
      const remoteEntryUrl = new URL('remoteEntry.js', baseUrl);
      remoteEntryUrl.searchParams.set('t', Date.now().toString());
      const remote = (await import(/* webpackIgnore: true */ remoteEntryUrl.href)) as FederationContainer;
      await remote.init({});
      const factory = await remote.get('./App');
      const chatModule = factory();
      if (
        typeof chatModule !== 'object' || chatModule === null ||
        !('acquireSilentToken' in chatModule) || typeof chatModule.acquireSilentToken !== 'function' ||
        !('mount' in chatModule) || typeof chatModule.mount !== 'function' ||
        !('unmount' in chatModule) || typeof chatModule.unmount !== 'function'
      ) {
        throw new Error(
          'The loaded Nexus remote is incompatible with TaxPortal silent sign-in. ' +
          'Deploy the updated chatbot frontend, clear the cached remoteEntry.js, and reload TaxPortal.',
        );
      }
      return chatModule as ChatRemoteModule;
    })();
    remoteModulePromise.catch(() => {
      remoteModulePromise = null;
    });
  }
  return remoteModulePromise;
}

const NexusChatLauncher: React.FC = () => {
  const mountElement = useRef<HTMLDivElement>(null);
  const unmountRemote = useRef<(() => void) | null>(null);
  const preloadedToken = useRef<{
    email: string;
    promise: Promise<{ token: string | null; acquiredAt: number }>;
  } | null>(null);
  const { user } = useContext(OidcIdentityContext);
  const cemUser: unknown = useRecoilValue(userCEMState);
  const role = getNexusChatRole(cemUser);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remoteUrl = envProperties.NexusChatRemoteUrl;
  const appId = envProperties.NexusChatAppId?.trim();
  const userName = user?.profile.name ?? user?.profile.preferred_username;
  const userEmail = user?.profile.email ?? user?.profile.preferred_username;

  // Keep the panel below TaxPortal's sticky header and right of its left navigation.
  useLayoutEffect(() => {
    const element = mountElement.current;
    if (!element) return;
    const updateOffset = () => {
      const header = document.querySelector('.cui-header');
      const main = document.getElementById('cui-layout_main');
      const footer = document.querySelector('.page-footer');
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const left = main ? Math.max(0, main.getBoundingClientRect().left) : 0;
      // Only the part of the footer currently scrolled into view needs to be reserved.
      const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;
      const bottom = Math.min(Math.max(0, window.innerHeight - footerTop), Math.max(0, window.innerHeight - top));
      element.style.setProperty('--taxportal-nexus-top', `${top}px`);
      element.style.setProperty('--taxportal-nexus-left', `${left}px`);
      element.style.setProperty('--taxportal-nexus-bottom', `${bottom}px`);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    const header = document.querySelector('.cui-header');
    const main = document.getElementById('cui-layout_main');
    const footer = document.querySelector('.page-footer');
    if (header) observer.observe(header);
    if (main) observer.observe(main);
    if (footer) observer.observe(footer);
    window.addEventListener('resize', updateOffset);
    window.addEventListener('scroll', updateOffset, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOffset);
      window.removeEventListener('scroll', updateOffset, true);
    };
  }, []);

  // Close only hides the chatbot so the conversation and loaded code survive reopening.
  const closeChat = useCallback(() => setOpen(false), []);

  const destroyChat = useCallback(() => {
    unmountRemote.current?.();
    unmountRemote.current = null;
    setOpen(false);
  }, []);

  useEffect(() => destroyChat, [destroyChat]);

  // Role or user changes invalidate the mounted session; the user reopens with fresh options.
  useEffect(() => {
    destroyChat();
  }, [destroyChat, role, userEmail, userName]);

  const authRedirectUri = `${window.location.origin}/nexus-auth-callback.html`;

  // Download the remote and get a token in the background once the page has loaded.
  useEffect(() => {
    if (!remoteUrl || !appId || !userEmail) return;
    let cancelled = false;
    const preload = () => {
      if (cancelled || preloadedToken.current) return;
      const tokenPromise = loadChatRemote(remoteUrl)
        .then((chat) => chat.acquireSilentToken({ loginHint: userEmail, authRedirectUri }))
        .then((token) => ({ token, acquiredAt: Date.now() }));
      preloadedToken.current = { email: userEmail, promise: tokenPromise };
      // A failed preload is retried when the user clicks the bubble.
      tokenPromise.catch(() => {
        if (preloadedToken.current?.promise === tokenPromise) preloadedToken.current = null;
      });
    };
    if (document.readyState === 'complete') {
      const timer = window.setTimeout(preload, 0);
      return () => { cancelled = true; window.clearTimeout(timer); };
    }
    window.addEventListener('load', preload, { once: true });
    return () => { cancelled = true; window.removeEventListener('load', preload); };
  }, [remoteUrl, appId, userEmail, authRedirectUri]);

  // Reuse the background token while it is fresh; otherwise ask again (MSAL's cache keeps this fast).
  const getInitialToken = async (acquireToken: () => Promise<string | null>) => {
    const preload = preloadedToken.current;
    if (preload && preload.email === userEmail) {
      try {
        const { token, acquiredAt } = await preload.promise;
        if (Date.now() - acquiredAt < PRELOADED_TOKEN_MAX_AGE_MS) return token;
      } catch {
        // Fall through and retry on the click.
      }
    }
    return acquireToken();
  };

  const openChat = async () => {
    if (loading || open) return;
    setError(null);
    if (!remoteUrl || !appId) {
      setError('Nexus Chat is not configured. Set REACT_APP_NEXUS_CHAT_REMOTE_URL and REACT_APP_NEXUS_CHAT_APP_ID.');
      return;
    }
    // Already mounted and hidden: just show it again.
    if (unmountRemote.current) {
      setOpen(true);
      return;
    }
    setLoading(true);
    try {
      const chat = await loadChatRemote(remoteUrl);
      const acquireToken = () => chat.acquireSilentToken({
        loginHint: userEmail ?? '',
        authRedirectUri,
      });
      const accessToken = await getInitialToken(acquireToken);
      if (!mountElement.current) return;
      chat.mount(mountElement.current, {
        appId,
        role,
        accessToken,
        acquireAccessToken: accessToken ? async () => {
          const token = await acquireToken();
          if (!token) {
            throw new Error('Microsoft sign-in requires interaction. Reopen Nexus Chat to sign in again.');
          }
          return token;
        } : undefined,
        containerUser: {
          name: userName,
          email: userEmail,
        },
        useMemoryRouter: true,
        // Defer so the remote isn't unmounted from inside its own click handler.
        onClose: () => window.setTimeout(closeChat, 0),
        topOffset: 'var(--taxportal-nexus-top, 0px)',
        leftOffset: 'var(--taxportal-nexus-left, 0px)',
        bottomOffset: 'var(--taxportal-nexus-bottom, 0px)',
        authRedirectUri,
      });
      unmountRemote.current = chat.unmount;
      setOpen(true);
    } catch (mountError: unknown) {
      setError(mountError instanceof Error ? mountError.message : 'The chatbot could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`nexus-chat-launcher__mount${open ? '' : ' nexus-chat-launcher__mount--hidden'}`}
        ref={mountElement}
        aria-label="Nexus Chat"
        aria-hidden={!open}
      />
      {!open && (
        <div className="nexus-chat-launcher">
          {error && (
            <div className="nexus-chat-launcher__error" role="alert">
              <span>{error}</span>
              <button type="button" aria-label="Dismiss" onClick={() => setError(null)}>
                <span className="material-icons-outlined" aria-hidden="true">close</span>
              </button>
            </div>
          )}
          <button
            type="button"
            className="nexus-chat-launcher__bubble"
            aria-label={loading ? 'Opening Nexus Chat' : 'Open Nexus Chat'}
            title="Nexus Chat"
            aria-busy={loading}
            disabled={loading}
            onClick={openChat}
          >
            {loading ? (
              <span className="nexus-chat-launcher__spinner" aria-hidden="true" />
            ) : (
              <span className="material-icons-outlined" aria-hidden="true">chat</span>
            )}
          </button>
        </div>
      )}
    </>
  );
};

export default NexusChatLauncher;
